import { useEffect } from "react";
import { X, Star, Award, UserCheck, MessageSquareQuote } from "lucide-react";
import UserChip from "../common/UserChip";
import Markdown from "../common/Markdown";
import { CATEGORY_LABELS } from "../../lib/constants";

export default function ActivityDetailModal({ item, onClose }) {
  const { rating, cp } = item;
  const post = cp?.post || null;
  const category = post?.category ? CATEGORY_LABELS[post.category] : null;

  // No CP = other person completed Arthur's task → "COMPLETED BY"
  // Has CP = Arthur was the contributor and got rated → "RATED BY"
  const isCompleted = !cp;

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-walnut/40 backdrop-blur-sm" />

      <div
        className="relative w-full max-w-lg mx-4 max-h-[80vh] bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone/50 bg-cream/60 shrink-0">
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-lg font-bold text-walnut truncate">
              {post?.title || "Activity Detail"}
            </h2>
            <div className="flex items-center gap-2 mt-0.5">
              {category && (
                <span className="text-xs font-medium text-terracotta bg-terracotta/10 px-2 py-0.5 rounded-full">
                  {category}
                </span>
              )}
              {cp?.action_type && (
                <span className="text-xs text-walnut-light capitalize">{cp.action_type}</span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="ml-3 p-1.5 rounded-full hover:bg-stone/30 text-walnut-light hover:text-walnut transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {rating?.rater && (
            <section>
              <div className="flex items-center gap-2 mb-2">
                {isCompleted ? (
                  <UserCheck size={14} className="text-walnut-light" />
                ) : (
                  <MessageSquareQuote size={14} className="text-walnut-light" />
                )}
                <span className="text-xs font-semibold text-walnut-light uppercase tracking-wide">
                  {isCompleted ? "Completed by" : "Rated by"}
                </span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-cream/80 rounded-xl">
                <div className="min-w-0">
                  <UserChip user={rating.rater} link={false} />
                </div>
              </div>
            </section>
          )}

          {rating && (
            <section>
              <div className="flex items-center gap-2 mb-2">
                <Star size={14} className="text-amber" />
                <span className="text-xs font-semibold text-walnut-light uppercase tracking-wide">
                  Rating & Feedback
                </span>
              </div>
              <div className="p-4 bg-amber/5 rounded-xl border border-amber/20">
                <div className="flex gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      size={20}
                      className={s <= rating.stars ? "fill-amber text-amber" : "text-stone"}
                    />
                  ))}
                </div>
                {rating.feedback && (
                  <p className="text-sm text-walnut leading-relaxed italic">
                    "{rating.feedback}"
                  </p>
                )}
                <p className="text-xs text-walnut-light/60 mt-2">
                  {new Date(rating.created_at).toLocaleDateString("en-US", {
                    year: "numeric", month: "long", day: "numeric",
                  })}
                </p>
              </div>
            </section>
          )}

          {cp && (
            <section>
              <div className="flex items-center gap-2 mb-2">
                <Award size={14} className="text-sage" />
                <span className="text-xs font-semibold text-walnut-light uppercase tracking-wide">
                  Community Points Earned
                </span>
              </div>
              <div className="p-4 bg-sage/5 rounded-xl border border-sage/20">
                <span className="text-2xl font-display font-bold text-sage">
                  +{cp.points}
                </span>
                <span className="text-sm text-walnut-light ml-1.5">CP</span>
                <p className="text-xs text-walnut-light/60 mt-1">
                  {new Date(cp.created_at).toLocaleDateString("en-US", {
                    year: "numeric", month: "long", day: "numeric",
                  })}
                </p>
              </div>
            </section>
          )}

          {cp?.llm_reasoning && (
            <section>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold text-walnut-light uppercase tracking-wide">
                  Scoring Breakdown
                </span>
              </div>
              <div className="p-4 bg-cream/80 rounded-xl border border-stone/30">
                <Markdown content={cp.llm_reasoning} className="text-sm" />
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
